// Documents of people's profiles for a client — T152 (28 Sep 2026).
//
// Made from a Search (the Client PDF button, SearchScreen.tsx) or by the brain's agent
// (`client_profiles`), kept by the brain API as made, and opened on their own page
// (ClientDocumentScreen.tsx), which downloads the PDF and sends it to a client contact. The bytes
// come behind the login, so the page fetches them with its token and saves them itself, as the
// Profile PDF button does (profilePdf.ts).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ClientDocumentMade, ClientDocumentView } from '@vera/bff';
import { API_PREFIX, type RequestFn } from './core.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

/**
 * The most people one document presents: the service's `MAX_DOCUMENT_PEOPLE`, restated because
 * this app imports types from `@vera/bff` and never code (api/core.ts); clientDocuments.test.ts
 * pins the two together.
 */
export const MAX_DOCUMENT_PEOPLE = 10;

/** What a send answers: the route it took, and what it did in the brain's words. */
export interface ClientDocumentSent {
  id: string;
  channel: 'whatsapp';
  detail: string;
}

export const clientDocumentsApi = (request: RequestFn) => ({
  make: (candidateIds: readonly string[], searchId: string | null) =>
    request<ClientDocumentMade>(`${API_PREFIX}/client-documents`, {
      method: 'POST',
      body: { candidateIds, searchId },
    }),
  get: (documentId: string) =>
    request<ClientDocumentView>(`${API_PREFIX}/client-documents/${documentId}`),
  pdf: (documentId: string) =>
    request<ArrayBuffer>(`${API_PREFIX}/client-documents/${documentId}/pdf`, { binary: true }),
  send: (
    documentId: string,
    fields: {
      clientId: string;
      contactId: string;
      message: string;
    },
  ) =>
    // Never by email since T554b (30 Oct 2026); the API still asks for the channel.
    request<ClientDocumentSent>(`${API_PREFIX}/client-documents/${documentId}/sends`, {
      method: 'POST',
      body: { ...fields, channel: 'whatsapp' },
    }),
});

/**
 * One document. Never polled: a document does not change once it is made (the brain API keeps
 * it as the recruiter read it), so it is fetched once and kept while the page is open.
 */
export function useClientDocument(documentId: string): UseQueryResult<ClientDocumentView> {
  const request = useRequest();
  return useQuery({
    queryKey: ['client-document', documentId],
    queryFn: () => clientDocumentsApi(request).get(documentId),
    staleTime: Infinity,
    enabled: documentId !== '',
  });
}

/** Making one writes nothing a list on screen shows, so nothing is fetched again. */
export const useMakeClientDocument = () =>
  useConsoleMutation(
    (request, args: { candidateIds: readonly string[]; searchId: string | null }) =>
      clientDocumentsApi(request).make(args.candidateIds, args.searchId),
    [],
  );

/**
 * A send puts everyone on the Search forward there and, on WhatsApp, goes onto the
 * contact's thread (kadmon carries on, T423): the Search's lists, the people, the contact and the threads are fetched again.
 */
export const useSendClientDocument = () =>
  useConsoleMutation(
    (
      request,
      args: {
        documentId: string;
        clientId: string;
        contactId: string;
        message: string;
      },
    ) =>
      clientDocumentsApi(request).send(args.documentId, {
        clientId: args.clientId,
        contactId: args.contactId,
        message: args.message,
      }),
    // T423: the send clears a pause on the contact's thread, so the hold and the waiting counts too.
    [
      ...KEYS.search,
      ['contact'],
      ['conversation'],
      ['conversations'],
      ['hold'],
      ['waiting'],
      ['counts'],
    ],
  );
